
import { CartProduct } from '@/contexts/cart/types';
import { supabase } from '@/integrations/supabase/client';

// Cache for shipping settings to avoid repeated database calls
let shippingSettingsCache: { 
  price: number; 
  freeShippingThreshold: number; 
  timestamp: number 
} | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

let inFlightShippingFetch: Promise<{ price: number; freeShippingThreshold: number }> | null = null;


// Subscribers notified whenever shipping settings are loaded/invalidated so
// React consumers can re-render with real (non-default) values.
const shippingSettingsListeners = new Set<() => void>();

const notifyShippingSettingsChanged = () => {
  shippingSettingsListeners.forEach((listener) => listener());
};

export const subscribeToShippingSettings = (listener: () => void) => {
  shippingSettingsListeners.add(listener);
  return () => {
    shippingSettingsListeners.delete(listener);
  };
};

export const areShippingSettingsLoaded = () => shippingSettingsCache !== null;

// Function to invalidate cache
export const invalidateShippingCache = () => {
  shippingSettingsCache = null;
  notifyShippingSettingsChanged();
};


export const getCartCount = (cartItems: CartProduct[]): number => {
  return cartItems.reduce((count, item) => count + item.quantity, 0);
};

// Legacy function - prefer calculateCartTotalWithPricing for current prices
export const getCartTotal = (cartItems: CartProduct[]): number => {
  console.warn('Warning: getCartTotal uses stored prices, consider using calculateCartTotalWithPricing');
  return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
};

export const calculateCartTotalWithPricing = (
  cartItems: CartProduct[], 
  getProductPrice: (productId: number) => number
): number => {
  return cartItems.reduce((total, item) => {
    const currentPrice = getProductPrice(item.id);
    return total + (currentPrice * item.quantity);
  }, 0);
};

const DEFAULT_SHIPPING_PRICE = 5.0;
const DEFAULT_FREE_SHIPPING_THRESHOLD = 30;

const fetchShippingSettingsFromAdmin = async (): Promise<{ price: number; freeShippingThreshold: number }> => {
  // Check cache first
  const now = Date.now();
  if (shippingSettingsCache && (now - shippingSettingsCache.timestamp) < CACHE_DURATION) {
    return {
      price: shippingSettingsCache.price,
      freeShippingThreshold: shippingSettingsCache.freeShippingThreshold
    };
  }

  try {
    // admin_settings is admin-only under RLS, so customers must read the
    // public-safe shipping config through the security-definer RPC.
    const { data, error } = await supabase.rpc('get_shipping_config');

    if (error || !data) {
      console.log('No shipping settings found, using defaults', error);
      return { price: DEFAULT_SHIPPING_PRICE, freeShippingThreshold: DEFAULT_FREE_SHIPPING_THRESHOLD };
    }

    const config = (typeof data === 'string' ? JSON.parse(data) : data) as {
      price?: number | string | null;
      free_shipping_threshold?: number | string | null;
    };

    const parsedPrice = Number(config.price);
    const parsedThreshold = Number(config.free_shipping_threshold);

    const shippingPrice = Number.isFinite(parsedPrice) ? parsedPrice : DEFAULT_SHIPPING_PRICE;
    const freeShippingThreshold = Number.isFinite(parsedThreshold)
      ? parsedThreshold
      : DEFAULT_FREE_SHIPPING_THRESHOLD;

    // Update cache
    shippingSettingsCache = {
      price: shippingPrice,
      freeShippingThreshold: freeShippingThreshold,
      timestamp: now
    };
    notifyShippingSettingsChanged();


    return { price: shippingPrice, freeShippingThreshold };
  } catch (error) {
    console.error('Error fetching shipping settings:', error);
    return { price: DEFAULT_SHIPPING_PRICE, freeShippingThreshold: DEFAULT_FREE_SHIPPING_THRESHOLD };
  }
};

// Warm the cache as early as possible so sync calculations use real settings.
export const preloadShippingSettings = () => fetchShippingSettingsFromAdmin();

export const getShippingCost = async (cartItems: CartProduct[], getProductPrice?: (productId: number) => number): Promise<number> => {
  if (cartItems.length === 0) {
    return 0;
  }

  // Get shipping settings from admin
  const { price: adminShippingPrice, freeShippingThreshold } = await fetchShippingSettingsFromAdmin();
  
  // Use current prices if getProductPrice is provided, otherwise fallback to stored prices
  const total = getProductPrice 
    ? calculateCartTotalWithPricing(cartItems, getProductPrice)
    : getCartTotal(cartItems);
  
  // Free shipping over threshold
  if (total >= freeShippingThreshold) {
    return 0;
  }
  
  return adminShippingPrice;
};

// Synchronous version for immediate calculations (uses cached value or default)
export const getShippingCostSync = (cartItems: CartProduct[], getProductPrice?: (productId: number) => number): number => {
  if (cartItems.length === 0) {
    return 0;
  }

  // Use current prices if getProductPrice is provided, otherwise fallback to stored prices
  const total = getProductPrice 
    ? calculateCartTotalWithPricing(cartItems, getProductPrice)
    : getCartTotal(cartItems);
  
  // If no cache exists, trigger a single async fetch to populate it
  if (!shippingSettingsCache && !inFlightShippingFetch) {
    inFlightShippingFetch = fetchShippingSettingsFromAdmin().finally(() => {
      inFlightShippingFetch = null;
    });
  }

  
  // Use cached shipping settings or defaults (0 is a valid configured value)
  const shippingPrice = shippingSettingsCache?.price ?? DEFAULT_SHIPPING_PRICE;
  const freeShippingThreshold =
    shippingSettingsCache?.freeShippingThreshold ?? DEFAULT_FREE_SHIPPING_THRESHOLD;
  
  // Free shipping over threshold
  if (total >= freeShippingThreshold) {
    return 0;
  }
  
  return shippingPrice;
};

// Helper function to get the free shipping threshold
export const getFreeShippingThreshold = (): number => {
  return shippingSettingsCache?.freeShippingThreshold ?? DEFAULT_FREE_SHIPPING_THRESHOLD;
};
